import { Workflow, Webhook, MousePointerClick, Pencil, Play, Trash2, Loader2, GitBranch, Clock, Share2, Eye, FolderInput, FolderMinus } from "lucide-react";
import GlassContainer from "@/shared/components/glasscontainer";
import type AutomationSummary from "../models/AutomationSummary";

export interface AutomationCardProps {
  automation: AutomationSummary;
  running: boolean;
  deleting: boolean;
  onEdit: () => void;
  onRun: () => void;
  onDelete: () => void;
  onShare: () => void;
  /** Sacarla del workspace (no la elimina). Solo lo pasa la vista de un workspace propio. */
  onUnfile?: () => void;
  /** Elegir en qué workspace archivarla. Solo lo pasa la lista general, y solo si es propia. */
  onMoveToWorkspace?: () => void;
}

const TRIGGER_STYLES: Record<string, { icon: typeof Workflow; chip: string; tile: string; label: string }> = {
  Webhook: {
    icon: Webhook,
    chip: "bg-violet-500/10 text-violet-600 border-violet-500/20",
    tile: "from-violet-500 to-violet-600 shadow-violet-500/30",
    label: "Webhook",
  },
  Manual: {
    icon: MousePointerClick,
    chip: "bg-accent/10 text-accent border-accent/20",
    tile: "from-[#0071e3] to-[#0059b3] shadow-[#0071e3]/30",
    label: "Manual",
  },
};

const DEFAULT_TRIGGER_STYLE = {
  icon: Workflow,
  chip: "bg-accent/10 text-accent border-accent/20",
  tile: "from-[#0071e3] to-[#0059b3] shadow-[#0071e3]/30",
  label: "",
};

/** "hace 5 min", "hace 3 días"… fallback to the absolute date beyond a month. */
function timeAgo(iso: string): string {
  const date = new Date(iso);
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat("es", { numeric: "auto" });
  if (seconds < 60) return rtf.format(-seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return rtf.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 24) return rtf.format(-hours, "hour");
  const days = Math.round(hours / 24);
  if (days < 31) return rtf.format(-days, "day");
  return date.toLocaleDateString();
}

export function AutomationCard({ automation, running, deleting, onEdit, onRun, onDelete, onShare, onUnfile, onMoveToWorkspace }: AutomationCardProps) {
  const triggerStyle = TRIGGER_STYLES[automation.trigger.type] ?? {
    ...DEFAULT_TRIGGER_STYLE,
    label: automation.trigger.type,
  };
  const TriggerIcon = triggerStyle.icon;
  // Una automatización compartida se puede abrir y ejecutar, nada más: el resto lo rechaza el
  // backend con 403, así que no se ofrece.
  const { isOwner } = automation;

  return (
    <GlassContainer className="group relative rounded-3xl transition-all duration-500 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] overflow-hidden hover:border-white hover:bg-glass-shine hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.12)]">
      <div className="p-5 relative flex flex-col min-w-0">
        <div className="flex items-start gap-3.5 min-w-0">
          <div
            className={`flex size-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg transition-transform group-hover:scale-110 group-hover:rotate-3 duration-500 ${triggerStyle.tile}`}
          >
            <TriggerIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[15px] font-bold text-foreground/90 tracking-tight group-hover:text-accent transition-colors">
              {automation.name}
            </h3>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-foreground/40 font-medium">
              <Clock className="size-3 shrink-0" />
              <span className="truncate" title={new Date(automation.publishedAtUtc).toLocaleString()}>
                {timeAgo(automation.publishedAtUtc)}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={onEdit}
              className="size-8 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:bg-white hover:text-accent hover:shadow-md transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              title={isOwner ? "Editar" : "Ver (solo lectura)"}
            >
              {isOwner ? <Pencil className="size-3.5" /> : <Eye className="size-3.5" />}
            </button>
            {isOwner && onMoveToWorkspace && (
              <button
                onClick={onMoveToWorkspace}
                className="size-8 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:bg-white hover:text-accent hover:shadow-md transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                title={automation.workspaceId ? "Mover a otro workspace" : "Archivar en un workspace"}
              >
                <FolderInput className="size-3.5" />
              </button>
            )}
            {onUnfile && (
              <button
                onClick={onUnfile}
                className="size-8 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:bg-white hover:text-accent hover:shadow-md transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                title="Quitar del workspace (no la elimina)"
              >
                <FolderMinus className="size-3.5" />
              </button>
            )}
            {isOwner && (
              <button
                onClick={onShare}
                className="size-8 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:bg-accent hover:border-accent hover:text-white hover:shadow-md transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                title="Compartir"
              >
                <Share2 className="size-3.5" />
              </button>
            )}
            <button
              onClick={onRun}
              disabled={running}
              className="size-8 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:bg-emerald-500 hover:border-emerald-500 hover:text-white hover:shadow-md transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Ejecutar"
            >
              {running ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />}
            </button>
            {isOwner && (
              <button
                onClick={onDelete}
                disabled={deleting}
                className="size-8 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:bg-rose-500 hover:border-rose-500 hover:text-white hover:shadow-md transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Eliminar"
              >
                {deleting ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-1.5 flex-wrap min-w-0">
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border shrink-0 ${triggerStyle.chip}`}
          >
            <TriggerIcon className="size-3" />
            {triggerStyle.label.toUpperCase()}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border shrink-0 bg-gray/10 text-gray border-black/5">
            <GitBranch className="size-3" />
            {automation.nodeCount} {automation.nodeCount === 1 ? "NODO" : "NODOS"}
          </span>
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full border shrink-0 bg-gray/10 text-gray border-black/5">
            v{automation.version}
          </span>
          {!automation.workspaceId && isOwner && onMoveToWorkspace && (
            <button
              onClick={onMoveToWorkspace}
              className="inline-flex cursor-pointer items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border border-dashed shrink-0 bg-transparent text-foreground/40 border-black/15 hover:text-accent hover:border-accent/40 transition-all"
              title="Archivar en un workspace"
            >
              <FolderInput className="size-3" />
              SIN AGRUPAR
            </button>
          )}
          {!isOwner && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border shrink-0 bg-amber-500/10 text-amber-600 border-amber-500/20"
              title="Te la compartieron: podés verla y ejecutarla, pero no modificarla"
            >
              <Share2 className="size-3" />
              COMPARTIDA
            </span>
          )}
        </div>
      </div>
    </GlassContainer>
  );
}
